import { GetServerSidePropsContext, NextPageContext } from 'next';
import { z } from 'zod';
import { getServerCookie } from '../utils';
import { logger } from '@dist-marketplace-frontend/services';

export enum VERTICAL {
  FARMING = 'farming',
  MARKETPLACE = 'marketplace',
  MOTOR = 'motor',
}

export const VERTICAL_LABELS = {
  [VERTICAL.MOTOR]: 'Cars & Motor',
  [VERTICAL.MARKETPLACE]: 'Marketplace',
  [VERTICAL.FARMING]: 'Agri & Plant',
};

const VerticalSchema = z.nativeEnum(VERTICAL);

const LEGACY_VERTICAL_COOKIE_NAME = 'ddSiteFocus';
const VERTICAL_COOKIE_NAME = 'ddVertical';
const FIFTEEN_DAYS_IN_MILLISECONDS = 15 * 24 * 60 * 60 * 1000;

function updateVertical(focus: VERTICAL, domain: string) {
  if (typeof document !== 'undefined') {
    let cookieDomain = '';

    try {
      const url = new URL(domain);
      // We remove the subdomain www to stay consistent with how it was done previously
      // NB: This cookie might be used by our BE, another reason to keep removing the subdomain www.
      cookieDomain = url.hostname.replace('www.', '');
    } catch (error) {
      logger.error({
        error,
        message: 'Error while trying parse domain for vertical cookie',
      });
      return;
    }

    const expiresAt = new Date(Date.now() + FIFTEEN_DAYS_IN_MILLISECONDS);

    document.cookie = `${VERTICAL_COOKIE_NAME}=${focus.toLowerCase()}; expires=${expiresAt.toUTCString()}; path=/; domain=${cookieDomain};`;
  }
}

function getVerticalClient(): VERTICAL {
  const cookie = document.cookie;
  const vertical =
    cookie
      .split('; ')
      .find((row) => row.startsWith(`${VERTICAL_COOKIE_NAME}=`))
      ?.split('=')[1] ??
    // TODO: Remove this 4 months after it is deployed (that's the max time a cookie can be stored in the browser)
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${LEGACY_VERTICAL_COOKIE_NAME}=`))
      ?.split('=')[1];

  return parseVertical(vertical);
}

function migrateLegacyVerticalCookie() {
  const legacyVertical = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${LEGACY_VERTICAL_COOKIE_NAME}=`));

  const verticalValue = legacyVertical?.split('=')[1];

  if (!legacyVertical || !verticalValue || !isValidVertical(verticalValue)) {
    return;
  }

  updateVertical(verticalValue, window.location.href);
  removeCookie(
    LEGACY_VERTICAL_COOKIE_NAME,
    new URL(window.location.href).hostname.replace('www.', ''),
  );

  function removeCookie(name: string, domain: string) {
    // Remove the cookie by setting it to expire immediately
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
  }

  function isValidVertical(vertical: string): vertical is VERTICAL {
    return Object.values(VERTICAL).includes(vertical as VERTICAL);
  }
}

function getVerticalServer(
  ctx: GetServerSidePropsContext | NextPageContext,
): VERTICAL {
  const vertical =
    getServerCookie(VERTICAL_COOKIE_NAME, ctx, false) ??
    // TODO: Remove this 4 months after it is deployed (that's the max time a cookie can be stored in the browser)
    getServerCookie(LEGACY_VERTICAL_COOKIE_NAME, ctx, false);
  return parseVertical(vertical);
}

function parseVertical(vertical: string | undefined): VERTICAL {
  const parsedVertical = VerticalSchema.safeParse(vertical);

  if (vertical === 'everything') {
    return VERTICAL.MARKETPLACE;
  }

  if (parsedVertical.success) {
    return parsedVertical.data;
  }
  return VERTICAL.MOTOR;
}

export {
  updateVertical,
  getVerticalClient,
  getVerticalServer,
  parseVertical,
  migrateLegacyVerticalCookie,
};
